import { useState } from 'react';
import { useDomainApi } from '@ui/shared/domain/DomainApiContext';
import type {
  IsoDate,
  ObjectiveCategory,
  ObjectiveLevelOfEffort,
  ObjectivePriority,
  ObjectiveReminder,
} from '@contracts';
import { ChevronLeft, Plus } from '@ui/shared/icons';
import { DatePickerField } from '@ui/shared/primitives/DatePickerField';
import { localIsoDate } from '@ui/shared/format/date';
import {
  ActionRow,
  Field,
  PrimaryButton,
  SecondaryButton,
  inputStyle,
  validationHelperStyle,
} from '@ui/shared/primitives/FormControls';
import {
  CadenceStepper,
  CategoryPillSelect,
  EffortTileSelector,
  PrioritySegmentedControl,
  ReminderSchedulePicker,
} from '@ui/desktop/objective-form/FormPrimitives';
import { VisibilityTogglesSection } from '@ui/desktop/objective-form/VisibilityTogglesSection';

/**
 * "New objective" — reachable from the Today view for a returning user
 * (owner: "we need to move forward to this before calling this a minimum
 * functional app... add more objectives"). First-run onboarding
 * (FirstRunSetup) is a 3-step wizard appropriate for a first-time user with
 * nothing on screen yet; this is the same title/why/date/actions field set
 * collapsed onto one page (via the same shared FormControls — Field,
 * ActionRow, PrimaryButton/SecondaryButton, DatePickerField) since a
 * returning user adding their second/third objective doesn't need to be
 * walked through three separate screens. Same validation rules as
 * FirstRunSetup's actions step: every action field must have content before
 * submission (owner clarification, applies uniformly across both entry
 * points now).
 *
 * Category/level of effort/cadence goal/priority/reminder/visibility toggles
 * (owner-confirmed phone parity subset, brought over from
 * `ObjectiveFormScreenDesktop`'s Phase 5 field set) reuse that screen's own
 * `FormPrimitives`/`VisibilityTogglesSection` building blocks as pure
 * imports — none of them use fixed-pixel widths, so they fit phone's content
 * width unmodified. Single-shot create only: no draft/autosave lifecycle, no
 * Realism Engine sidebar (both explicitly out of scope for phone). These
 * fields can't be persisted via `createObjective` itself (its event schema
 * only carries title/why/targetDate/actions/initialStatus — see
 * `ensureCreated()`'s bug-fix comment in the desktop screen for why), so
 * submit follows the same two-call sequence that screen settled on: create,
 * then one `updateObjectiveDetails` call with everything else.
 */
export function NewObjectiveScreen({
  onDone,
  onCancel,
}: {
  readonly onDone: () => void;
  readonly onCancel: () => void;
}) {
  const api = useDomainApi();
  const [title, setTitle] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);
  const [why, setWhy] = useState('');
  const [targetDate, setTargetDate] = useState(defaultTargetDate());
  const [targetDateTouched, setTargetDateTouched] = useState(false);
  const [actionTitles, setActionTitles] = useState<string[]>(['']);
  const [actionsTouched, setActionsTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // -- Phase-6-parity fields (owner-confirmed subset, see header comment) --
  const [category, setCategory] = useState<ObjectiveCategory | null>(null);
  const [levelOfEffort, setLevelOfEffort] = useState<ObjectiveLevelOfEffort | null>(null);
  const [cadenceGoal, setCadenceGoal] = useState<number | null>(null);
  const [priority, setPriority] = useState<ObjectivePriority>('medium');
  const [reminder, setReminder] = useState<ObjectiveReminder | null>(null);
  const [includeInWeeklyReview, setIncludeInWeeklyReview] = useState(true);
  const [surfaceOnToday, setSurfaceOnToday] = useState(true);
  const [ambientRewardOnComplete, setAmbientRewardOnComplete] = useState(true);

  // Bug fix (timezone): toISOString() reads UTC, not device-local time
  // (ADR-0006) — same bug class fixed in TodayView.tsx; localIsoDate()
  // mirrors the domain layer's own ClockPort.
  const today = localIsoDate();
  const lastActionHasContent = (actionTitles[actionTitles.length - 1] ?? '').trim().length > 0;
  const hasAnyAction = actionTitles.some((t) => t.trim().length > 0);
  const hasBlankAction = actionTitles.some((t) => t.trim().length === 0);
  const canSubmit = title.trim().length > 0 && hasAnyAction && !hasBlankAction && !submitting;

  async function submit() {
    setTitleTouched(true);
    setActionsTouched(true);
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const actions = actionTitles
        .map((t) => t.trim())
        .filter((t) => t.length > 0)
        .map((t) => ({ title: t, dueDate: null, milestoneTag: null }));
      const newId = await api.createObjective({
        title: title.trim(),
        why: why.trim(),
        targetDate: targetDate as IsoDate,
        actions,
      });
      // createObjective's event schema only ever carries title/why/
      // targetDate/actions/initialStatus — category/effort/priority/
      // reminder/cadence/toggles ride on their own objective.edited event
      // via updateObjectiveDetails (see ObjectiveFormScreenDesktop's
      // ensureCreated() bug-fix comment for the full story).
      await api.updateObjectiveDetails(newId, {
        category,
        levelOfEffort,
        priority,
        reminder,
        cadenceGoalPerWeek: cadenceGoal,
        includeInWeeklyReview,
        surfaceOnToday,
        ambientRewardOnComplete,
      });
      onDone();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main
      aria-label="New objective"
      style={{ padding: 'var(--space-6) var(--layout-page-margin-phone) var(--space-8)', maxWidth: '480px', margin: '0 auto' }}
    >
      <button
        type="button"
        onClick={onCancel}
        aria-label="Back to Today"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-1)',
          color: 'var(--text-muted)',
          fontSize: 'var(--type-caption-size)',
          fontWeight: 600,
          minHeight: 'var(--touch-target-min)',
          marginLeft: '-8px',
        }}
      >
        <ChevronLeft size={16} aria-hidden="true" />
        Today
      </button>

      <h1
        style={{
          fontSize: 'var(--type-heading-size)',
          fontWeight: 'var(--type-heading-weight)',
          color: 'var(--text-primary)',
          marginTop: 'var(--space-3)',
        }}
      >
        New objective
      </h1>

      <Field label="Title">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => setTitleTouched(true)}
          placeholder="e.g. AWS certification"
          style={inputStyle}
        />
      </Field>
      {titleTouched && title.trim().length === 0 && (
        <p role="status" style={validationHelperStyle}>
          Give your objective a name to continue.
        </p>
      )}

      <Field label="Why (optional)">
        <input
          value={why}
          onChange={(e) => setWhy(e.target.value)}
          placeholder="e.g. career growth"
          style={inputStyle}
        />
      </Field>

      <Field label="Category">
        <CategoryPillSelect value={category} onChange={setCategory} />
      </Field>

      <Field label="Level of effort">
        <EffortTileSelector value={levelOfEffort} onChange={setLevelOfEffort} />
      </Field>

      <Field label="Target date" as="div">
        <DatePickerField
          value={targetDate}
          today={today}
          isDefaultValue={!targetDateTouched}
          onChange={(date) => {
            setTargetDate(date);
            setTargetDateTouched(true);
          }}
        />
      </Field>

      <Field label="Cadence goal">
        <CadenceStepper value={cadenceGoal} onChange={setCadenceGoal} />
      </Field>

      <Field label="Priority">
        <PrioritySegmentedControl value={priority} onChange={setPriority} />
      </Field>

      <Field label="Reminder">
        <ReminderSchedulePicker value={reminder} onChange={setReminder} />
      </Field>

      <div style={{ marginTop: 'var(--space-6)' }}>
        <h2
          style={{
            fontSize: 'var(--type-caption-size)',
            letterSpacing: 'var(--type-caption-tracking)',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            fontWeight: 600,
            margin: 0,
          }}
        >
          Actions
        </h2>
        {actionTitles.map((value, i) => {
          const rowBlank = value.trim().length === 0;
          return (
            <ActionRow
              key={i}
              label={`Action ${i + 1}`}
              value={value}
              onChange={(next) => {
                const nextTitles = [...actionTitles];
                nextTitles[i] = next;
                setActionTitles(nextTitles);
              }}
              onBlur={() => setActionsTouched(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submit();
              }}
              canRemove={actionTitles.length > 1}
              onRemove={() => setActionTitles((prev) => prev.filter((_, idx) => idx !== i))}
              helperText={
                actionsTouched && rowBlank ? (
                  <p role="status" style={validationHelperStyle}>
                    Give this action a name, or remove the row.
                  </p>
                ) : undefined
              }
            />
          );
        })}
        <button
          type="button"
          onClick={() => setActionTitles((prev) => [...prev, ''])}
          disabled={!lastActionHasContent}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            color: lastActionHasContent ? 'var(--hue-primary)' : 'var(--text-disabled)',
            fontSize: 'var(--type-caption-size)',
            fontWeight: 600,
            marginTop: 'var(--space-2)',
            minHeight: 'var(--touch-target-min)',
          }}
        >
          <Plus size={16} aria-hidden="true" />
          Add another action
        </button>
        {actionsTouched && !hasAnyAction && (
          <p role="status" style={validationHelperStyle}>
            Add at least one action to get started.
          </p>
        )}
      </div>

      <VisibilityTogglesSection
        includeInWeeklyReview={includeInWeeklyReview}
        surfaceOnToday={surfaceOnToday}
        ambientRewardOnComplete={ambientRewardOnComplete}
        onChange={(changes) => {
          if (changes.includeInWeeklyReview !== undefined) setIncludeInWeeklyReview(changes.includeInWeeklyReview);
          if (changes.surfaceOnToday !== undefined) setSurfaceOnToday(changes.surfaceOnToday);
          if (changes.ambientRewardOnComplete !== undefined) setAmbientRewardOnComplete(changes.ambientRewardOnComplete);
        }}
      />

      <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-6)' }}>
        <PrimaryButton onClick={() => void submit()} disabled={!canSubmit}>
          Create objective
        </PrimaryButton>
        <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
      </div>
    </main>
  );
}

/**
 * today + 30 days, 'YYYY-MM-DD' — same default as FirstRunSetup.
 *
 * Bug fix (timezone): `setDate` is local-time-correct, but the previous
 * `toISOString().slice(0, 10)` formatted the result as UTC — in any
 * timezone behind UTC this could pre-fill a date one day later than
 * intended, silently submitted if the user never touches the field.
 * `localIsoDate()` mirrors the domain layer's own `ClockPort`.
 */
function defaultTargetDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return localIsoDate(d);
}
